/**
 * The catalog's API as the editor uses it: models by id, search by tags, and the web
 * conversion queue (`/models/web`) that turns a model's Unity bundle into the GLB the editor
 * renders. Everything here is plain fetch; the shared state of the queue lives in this module
 * so the parser, the 3D viewer and the interface see one answer per model.
 *
 * Model ids are the editor's own (`modelRef.id`): a cloud model's 24-hex id, or a built-in's
 * Unity path such as `sleeping-lying.bedroom.beds.ikea/001/001`. The catalog stores the
 * latter as `builtinId` with the prefix `Models/` and every dot encoded — `catalogKeyOf`
 * makes the translation, `modelIdOfKey` reverses it.
 */
import { t } from '../i18n/I18n';

export const CATALOG_API = ((import.meta.env?.VITE_CATALOG_API as string | undefined) ?? 'https://icandesignapp.com').replace(/\/$/, '');

/** A model document as the catalog answers it; only the fields the editor reads. */
export interface CatalogModel {
  id: string;
  title?: string;
  /** Catalog size in centimetres. */
  width?: number;
  height?: number;
  depth?: number;
  tags?: string[];
  categories?: string[];
  thumbs?: Array<{ name?: string; url?: string }>;
  bundles?: Record<string, string>;
  /** The legacy windows bundle, on models that predate `bundles`. */
  bundle?: string;
  definition?: string;
  builtinId?: string;
  /** The account that published it; the app's own built-ins have none. */
  accountId?: string;
  is2d?: boolean;
  public?: boolean;
  webProcessing?: { status?: string; error?: string };
}

/**
 * Where a model stands with the web queue. `done` carries the GLB `url`; `error` the reason
 * people can act on; `none` is a model nobody has asked for yet; `missing` is a model the
 * catalog does not have; `unavailable` means the catalog could not be reached, which the
 * editor treats as missing for now and says so.
 */
export interface WebState {
  status: 'queued' | 'running' | 'done' | 'error' | 'none' | 'missing' | 'unavailable';
  url?: string;
  /** The reason as text for the log; people are told by `code` (`permanentFailure`). */
  error?: string;
  /** Why an `error` failed (`WebErrorCode`); absent from a catalog that predates the codes. */
  code?: string;
  lastError?: string;
  /** Why the re-conversion of a `done` model failed, its previous bundle still handed out. */
  lastCode?: string;
  attempts?: number;
  requested?: string;
  finished?: string;
  /** The GLB layout of a `done` bundle; absent from a catalog that predates it, which means 1. */
  format?: number;
}

/**
 * The GLB layout this editor reads best (the worker's FORMAT). A bundle of an older one still
 * loads, and is asked for again so the catalog converts it behind the one it hands out.
 */
export const WEB_FORMAT = 2;

/** Why the queue failed a model (the catalog's `WebErrorCode`). */
export type WebErrorCode = 'no-definition' | 'no-source' | 'file-not-loaded' | 'no-geometry' | 'timeout' | 'conversion-failed';
/** The catalog's PERMANENT_ERRORS: they hold until the model itself changes, and the catalog answers them unchanged till then. */
const PERMANENT_CODES: ReadonlySet<string> = new Set<WebErrorCode>(['no-definition', 'no-source', 'file-not-loaded', 'no-geometry']);

/** Whether a failure holds until the model changes; any other code, or none from an older catalog, may pass on a new try. */
export function permanentFailure(code: string | undefined): boolean {
  return !!code && PERMANENT_CODES.has(code);
}

export interface CatalogKey { key: string; builtin: boolean }

const CLOUD_ID = /^[0-9a-f]{24}$/i;
/** Ids per catalog call; the same ceiling the API has. */
const MAX_IDS = 200;
/** Built-in ids are long paths and travel in a query string: fewer per call. */
const MAX_BUILTIN_IDS = 40;

/**
 * The app's `ModelsApi.MongoEscape`: a built-in's path is stored with `$` and `.` hex-escaped
 * (`Uri.HexEscape`, upper case), nothing else touched. `mongoUnescape` is its inverse.
 */
export function mongoEscape(path: string): string { return path.replace(/\$/g, '%24').replace(/\./g, '%2E'); }
export function mongoUnescape(key: string): string { return key.replace(/%24/gi, '$').replace(/%2E/gi, '.'); }

/** The catalog's key for an editor model id, or null for a procedural region. */
export function catalogKeyOf(modelId: string): CatalogKey | null {
  if (!modelId) return null;
  if (CLOUD_ID.test(modelId)) return { key: modelId.toLowerCase(), builtin: false };
  if (modelId.includes('-region-')) return null;
  const path = modelId.replace(/^Models\//, '');
  // A Unity Resources path: anything printable, no control characters, no way up the tree.
  if (!path || /[\u0000-\u001f]/.test(path) || path.split('/').some((part) => !part || part === '..')) return null;
  return { key: 'Models/' + mongoEscape(path), builtin: true };
}

/** The editor model id a catalog key or document stands for. */
export function modelIdOfKey(key: string, builtin: boolean): string {
  return builtin ? mongoUnescape(key.replace(/^Models\//, '')) : key.toLowerCase();
}

export function modelIdOf(model: CatalogModel): string {
  return model.builtinId ? modelIdOfKey(model.builtinId, true) : model.id;
}

export type Fetch = typeof fetch;
let fetchImpl: Fetch | null = null;
/** Tests hand in their own fetch; the page uses the browser's. */
export function useCatalogFetch(impl: Fetch | null): void { fetchImpl = impl; }
const doFetch: Fetch = (input, init) => (fetchImpl ?? fetch)(input, init);

async function getJson<T>(path: string): Promise<T> {
  const response = await doFetch(`${CATALOG_API}${path}`, { headers: { accept: 'application/json' } });
  if (!response.ok) throw new Error(t('#CatalogApiHttpError', response.status, path.split('?')[0]));
  return response.json() as Promise<T>;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await doFetch(`${CATALOG_API}${path}`, {
    method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(t('#CatalogApiHttpError', response.status, path));
  return response.json() as Promise<T>;
}

/** The project an invitation opens, as the catalog's anonymous preview names it. */
export interface SharePreview { projectId: string; title: string }

/** `GET /collab/share/:token/preview`: null for a link that is off or unknown; an outage rejects. */
export async function fetchSharePreview(token: string): Promise<SharePreview | null> {
  const response = await doFetch(`${CATALOG_API}/collab/share/${encodeURIComponent(token)}/preview`, { headers: { accept: 'application/json' } });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(t('#CatalogApiHttpError', response.status, '/collab/share/preview'));
  const answer = await response.json() as { projectId?: unknown; title?: unknown } | null;
  const projectId = catalogProjectId(typeof answer?.projectId === 'string' ? answer.projectId : null);
  return projectId ? { projectId, title: typeof answer?.title === 'string' ? answer.title : '' } : null;
}

/** The rt room an account may join: its address, the account's role, and a one-use ticket for a browser's socket. */
export type CollabSession = { kind: 'ok'; url: string; role: string; ticket?: string } | { kind: 'signed-out' } | { kind: 'closed' };

/**
 * `GET /collab/:projectId/session` with the web's token and the invitation's link token in headers, never in the address
 * (the catalog gives the link's role only to whoever shows its token): 401 is a sign-in that ended, 403/404 an access that
 * is gone; an outage or a 5xx rejects with its `status`, to be asked again.
 */
export async function fetchCollabSession(projectId: string, token: string, linkToken: string, signal?: AbortSignal): Promise<CollabSession> {
  const response = await doFetch(`${CATALOG_API}/collab/${encodeURIComponent(projectId)}/session`, {
    headers: { accept: 'application/json', authorization: `Bearer ${token}`, 'x-collab-link': linkToken }, signal,
  });
  if (response.status === 401) return { kind: 'signed-out' };
  if (response.status === 403 || response.status === 404) return { kind: 'closed' };
  if (!response.ok) throw Object.assign(new Error(t('#CatalogApiHttpError', response.status, '/collab/session')), { status: response.status });
  const answer = await response.json() as { url?: unknown; myRole?: unknown; ticket?: unknown } | null;
  if (typeof answer?.url !== 'string' || !answer.url) throw Object.assign(new Error(t('#CatalogApiHttpError', response.status, '/collab/session')), { status: 502 });
  return { kind: 'ok', url: answer.url, role: typeof answer.myRole === 'string' ? answer.myRole : 'can_view',
    ...(typeof answer.ticket === 'string' && answer.ticket ? { ticket: answer.ticket } : {}) };
}

/** A role in a shared project as the catalog names it (`collab.role.js`). */
export type CollabRole = 'owner' | 'can_edit' | 'can_view' | 'no_access';
export interface CollabAccount { accountId: string; name: string; avatar?: string }
/** `awaiting_registration`: an address nobody has signed in with yet; `invited`: an account that has not opened the project yet. */
export interface CollabMember { accountId: string | null; name: string; avatar?: string; email?: string; role: CollabRole; status: 'active' | 'invited' | 'awaiting_registration' }
/** `GET /collab/:projectId`. The catalog gives `shareUrl` to the owner and to link visitors only, e-mails to the owner only. */
export interface CollabProject { projectId: string; globalRole: CollabRole; shareUrl?: string; myRole: CollabRole; me: CollabAccount; owner: CollabAccount; members: CollabMember[] }
export type CollabErrorCode = 'signed-out' | 'forbidden' | 'gone' | 'conflict' | 'failed';
/** `reason` and `limit` on a refused invitation (409), read from the server's answer: full, the owner's own address, or busy. */
export type CollabError = Error & { status?: number; code: CollabErrorCode; reason?: 'limit' | 'owner' | 'busy'; limit?: number };

const COLLAB_CODES: Record<number, CollabErrorCode> = { 401: 'signed-out', 403: 'forbidden', 404: 'gone', 409: 'conflict' };
const COLLAB_ROLES: ReadonlySet<string> = new Set<CollabRole>(['owner', 'can_edit', 'can_view', 'no_access']);
const MEMBER_STATUSES: ReadonlySet<string> = new Set(['active', 'invited', 'awaiting_registration']);

function collabError(status: number | undefined, route: string): CollabError {
  return Object.assign(new Error(t('#CatalogApiHttpError', status ?? 0, route)), { status, code: (status && COLLAB_CODES[status]) || 'failed' as const });
}

/** A request with the web's token, and the invitation's link token when there is one, in headers only. */
async function collabFetch(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, route: string, token: string,
  { body, link, signal }: { body?: unknown; link?: string; signal?: AbortSignal } = {}): Promise<unknown> {
  let response: Response;
  try {
    response = await doFetch(`${CATALOG_API}${path}`, { method, signal, headers: { accept: 'application/json', authorization: `Bearer ${token}`,
      ...(link ? { 'x-collab-link': link } : {}), ...(body !== undefined ? { 'content-type': 'application/json' } : {}) },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
  } catch (error) {
    if (signal?.aborted) throw error;
    throw collabError(undefined, route);
  }
  if (!response.ok) {
    const detail = await response.json().then(answer => text((answer as { message?: unknown } | null)?.message), () => '');
    throw Object.assign(collabError(response.status, route), { detail });
  }
  try { return await response.json(); } catch { throw collabError(502, route); }
}

/** A refused invitation by the catalog's wording (`collaboration.ctrl.js`): «limited to N people», «Cannot invite the owner», else try again. */
async function inviting(send: Promise<unknown>): Promise<void> {
  try { await send; } catch (error) {
    const failure = error as CollabError & { detail?: string };
    if (failure.code === 'conflict') {
      const limit = /limited to (\d+)/i.exec(failure.detail ?? '')?.[1];
      failure.reason = limit ? 'limit' : /owner/i.test(failure.detail ?? '') ? 'owner' : 'busy';
      if (limit) failure.limit = Number(limit);
    }
    throw failure;
  }
}

const collabPath = (projectId: string, rest = '') => '/collab/' + encodeURIComponent(projectId) + rest;

const text = (value: unknown): string => typeof value === 'string' ? value : '';
const collabRole = (value: unknown): CollabRole => COLLAB_ROLES.has(text(value)) ? value as CollabRole : 'can_view';

function collabAccount(raw: unknown): CollabAccount {
  const value = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return { accountId: text(value.accountId), name: text(value.name), ...(text(value.avatar) ? { avatar: text(value.avatar) } : {}) };
}

function collabMember(raw: unknown): CollabMember | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>, account = collabAccount(value);
  return { accountId: account.accountId || null, name: account.name, ...(account.avatar ? { avatar: account.avatar } : {}),
    ...(text(value.email) ? { email: text(value.email) } : {}), role: collabRole(value.role),
    status: MEMBER_STATUSES.has(text(value.status)) ? value.status as CollabMember['status'] : 'active' };
}

/** Who has the shared project and how the link admits: 404 for anyone the project does not let in, a removed member too. */
export async function fetchCollabProject(projectId: string, token: string, linkToken?: string, signal?: AbortSignal): Promise<CollabProject> {
  const answer = await collabFetch('GET', collabPath(projectId), '/collab/:projectId', token, { link: linkToken, signal }) as Record<string, unknown> | null;
  if (!answer || typeof answer !== 'object' || !text(answer.projectId)) throw collabError(502, '/collab/:projectId');
  return { projectId: text(answer.projectId), globalRole: collabRole(answer.globalRole), myRole: collabRole(answer.myRole),
    ...(text(answer.shareUrl) ? { shareUrl: text(answer.shareUrl) } : {}), me: collabAccount(answer.me), owner: collabAccount(answer.owner),
    members: (Array.isArray(answer.members) ? answer.members : []).flatMap(member => collabMember(member) ?? []) };
}

/** The owner's link, made on first use (`POST /collab`); 409 while the room's scene is not stored yet. */
export async function ensureCollabShare(projectId: string, token: string): Promise<{ shareUrl: string; globalRole: CollabRole }> {
  const answer = await collabFetch('POST', '/collab', '/collab', token, { body: { projectId } }) as Record<string, unknown> | null;
  if (!text(answer?.shareUrl)) throw collabError(502, '/collab');
  return { shareUrl: text(answer!.shareUrl), globalRole: collabRole(answer!.globalRole) };
}

/** A member's role (`PUT …/users/:accountId/role`); an invitation by address has no account and no role of its own to change. */
export async function setCollabRole(projectId: string, token: string, accountId: string, role: 'can_edit' | 'can_view'): Promise<void> {
  await collabFetch('PUT', collabPath(projectId, `/users/${encodeURIComponent(accountId)}/role`), '/collab/:projectId/users/:accountId/role', token, { body: { role } });
}

/** Who the link admits besides the people invited (`PUT …/globalRole`). */
export async function setCollabGlobalRole(projectId: string, token: string, globalRole: 'can_edit' | 'can_view' | 'no_access'): Promise<void> {
  await collabFetch('PUT', collabPath(projectId, '/globalRole'), '/collab/:projectId/globalRole', token, { body: { globalRole } });
}

/** Takes a member's access away, by account or, for an invitation nobody signed in with yet, by its address; 404 when they are gone already. */
export async function removeCollabUser(projectId: string, token: string, target: string): Promise<void> {
  await collabFetch('DELETE', collabPath(projectId, `/users/${encodeURIComponent(target)}`), '/collab/:projectId/users/:accountId', token);
}

/** Someone found by name or address (`GET /accounts/search`): known people first; an address finds at most its own account. */
export interface AccountMatch { id: string; name: string; avatar?: string }

export async function searchAccounts(term: string, token: string, signal?: AbortSignal): Promise<AccountMatch[]> {
  const answer = await collabFetch('GET', `/accounts/search?${new URLSearchParams({ term })}`, '/accounts/search', token, { signal });
  return (Array.isArray(answer) ? answer : []).flatMap(row => {
    const value = (row && typeof row === 'object' ? row : {}) as Record<string, unknown>;
    return text(value.id) ? [{ id: text(value.id), name: text(value.username), ...(text(value.avatar) ? { avatar: text(value.avatar) } : {}) }] : [];
  });
}

/** Invites an account at once, as the app does; 409 when the project is full (25 people) or busy. */
export async function inviteCollabAccount(projectId: string, token: string, accountId: string, role: 'can_edit' | 'can_view'): Promise<void> {
  await inviting(collabFetch('POST', collabPath(projectId, `/users/${encodeURIComponent(accountId)}/invite`), '/collab/:projectId/users/:accountId/invite', token, { body: { role } }));
}

/** Invites an address nobody may have signed in with yet: listed as pending until someone signs in with it. */
export async function inviteCollabEmail(projectId: string, token: string, email: string, role: 'can_edit' | 'can_view'): Promise<void> {
  await inviting(collabFetch('POST', collabPath(projectId, '/invite-email'), '/collab/:projectId/invite-email', token, { body: { email, role } }));
}

/** A member gives up their access (`POST …/leave`); 404, nothing to leave, is taken as left, as the app's `LeaveSharedProject` does. */
export async function leaveCollab(projectId: string, token: string): Promise<void> {
  try { await collabFetch('POST', collabPath(projectId, '/leave'), '/collab/:projectId/leave', token, { body: {} }); } catch (error) {
    if ((error as CollabError).code !== 'gone') throw error;
  }
}

function chunks<T>(list: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/**
 * Documents by raw catalog keys of built-ins (the `builtinId` as stored: `Assets/Resources/…`
 * with `.` and `$` escaped), keyed by those keys. Unknown keys are simply absent.
 */
export async function fetchBuiltinDocuments(keys: string[]): Promise<Record<string, CatalogModel>> {
  const out: Record<string, CatalogModel> = {};
  for (const part of chunks([...new Set(keys)], MAX_BUILTIN_IDS)) {
    const ids = part.map((k) => encodeURIComponent(k)).join(',');
    const answer = await getJson<Record<string, CatalogModel>>(`/models/${ids}?isBuiltin=1`);
    for (const model of Object.values(answer)) if (model && typeof model === 'object' && model.builtinId) out[model.builtinId] = model;
  }
  return out;
}

/** Model documents by editor ids, keyed by those ids. Unknown ids are simply absent. */
export async function fetchModels(modelIds: string[]): Promise<Record<string, CatalogModel>> {
  const out: Record<string, CatalogModel> = {};
  const groups = groupKeys(modelIds);
  for (const [builtin, keys] of groups) {
    for (const part of chunks(keys, builtin ? MAX_BUILTIN_IDS : MAX_IDS)) {
      const ids = part.map((k) => encodeURIComponent(k)).join(',');
      const answer = await getJson<Record<string, CatalogModel>>(`/models/${ids}${builtin ? '?isBuiltin=1' : ''}`);
      for (const model of Object.values(answer)) if (model && typeof model === 'object') out[modelIdOf(model)] = model;
    }
  }
  return out;
}

export interface SearchQuery {
  /** Any of these tags (the catalog's `orTags`). */
  anyTags?: string[];
  /** All of these tags (`tags`). */
  allTags?: string[];
  term?: string;
  skip?: number;
  pagesize?: number;
  /** Only what people published themselves, leaving the app's own built-ins out. */
  publishedByPeople?: boolean;
  /** Only the app's own built-ins (models without an account). */
  builtinOnly?: boolean;
}

/** Published models matching tags and/or a term, in the catalog's own order. */
export async function searchModels(query: SearchQuery): Promise<CatalogModel[]> {
  const params = new URLSearchParams();
  if (query.anyTags?.length) params.set('orTags', query.anyTags.join(','));
  if (query.allTags?.length) params.set('tags', query.allTags.join(','));
  if (query.term) params.set('term', query.term);
  if (query.skip) params.set('skip', String(query.skip));
  params.set('pagesize', String(query.pagesize ?? 40));
  // The catalog reads the mere presence of these parameters as "yes".
  if (!query.publishedByPeople) params.set('includeWithoutAccount', 'true');
  if (query.builtinOnly) params.set('onlyWithoutAccount', 'true');
  const answer = await getJson<CatalogModel[] | { models?: CatalogModel[] }>(`/models/search?${params}`);
  return Array.isArray(answer) ? answer : answer.models ?? [];
}

/** The picture the catalog shows for a model: the app's own render first, any other after. */
export function thumbnailOf(model: CatalogModel): string | undefined {
  const thumbs = model.thumbs ?? [];
  return (thumbs.find((t) => t.name === '1') ?? thumbs.find((t) => t.url))?.url;
}

// ---------------------------------------------------------------------------
// The web queue: one shared answer per model, kept current by a single poller.

const states = new Map<string, WebState>();
const listeners = new Set<(modelId: string, state: WebState) => void>();
const pending = new Set<string>();
let pollTimer: ReturnType<typeof setTimeout> | undefined;
let pollDelay = 0;
let pollStarted = 0;
const POLL_DELAYS_MS = [2000, 4000, 8000, 15000];
/** After this long without an answer for a model the editor stops waiting for it. */
const POLL_GIVE_UP_MS = 10 * 60 * 1000;

/**
 * What the queue is asked on behalf of (`setCatalogScope`): the catalog project the open scene came from, or the 3D
 * estimate link the product viewer shows (`snapshot` = its token). Named in every question to the queue.
 */
export interface CatalogScope { project?: string | null; snapshot?: string | null }
let catalogProject: string | null = null;
let catalogSnapshot: string | null = null;

/** The first of these that is a catalog project's id (24 hex), or null: a local or shared project's own id is not one. */
export function catalogProjectId(...candidates: Array<string | null | undefined>): string | null {
  return candidates.find((id): id is string => !!id && CLOUD_ID.test(id))?.toLowerCase() ?? null;
}

/**
 * The catalog project the editor works from (`GET /projects/:id/project`), or null. The queue answers a model outside
 * the public catalog — unpublished, deleted, a company's — only for a project that shows it, `missing` otherwise, so the
 * project goes with every request and status. A `missing` heard for another project is forgotten, to be asked again.
 */
export function setCatalogProject(id: string | null): void {
  setCatalogScope({ project: id });
}

/** The scope of every later question to the queue; a change forgets the `missing` heard under the previous one. */
export function setCatalogScope(scope: CatalogScope | null): void {
  const project = catalogProjectId(scope?.project);
  const snapshot = typeof scope?.snapshot === 'string' && scope.snapshot ? scope.snapshot : null;
  if (project === catalogProject && snapshot === catalogSnapshot) return;
  catalogProject = project;
  catalogSnapshot = snapshot;
  for (const [modelId, state] of states) if (state.status === 'missing') states.delete(modelId);
}

function scopeQuery(): string {
  return `${catalogProject ? `&project=${catalogProject}` : ''}${catalogSnapshot ? `&snapshot=${encodeURIComponent(catalogSnapshot)}` : ''}`;
}

function scopeBody(): CatalogScope {
  return { ...(catalogProject ? { project: catalogProject } : {}), ...(catalogSnapshot ? { snapshot: catalogSnapshot } : {}) };
}

export function webStateOf(modelId: string): WebState | undefined { return states.get(modelId); }

/** Every model the editor knows the queue's answer for. */
export function webStates(): ReadonlyMap<string, WebState> { return states; }

export function subscribeWebStates(listener: (modelId: string, state: WebState) => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function publish(modelId: string, state: WebState): void {
  const before = states.get(modelId);
  states.set(modelId, state);
  if (state.status === 'queued' || state.status === 'running') pending.add(modelId); else pending.delete(modelId);
  if (before?.status === state.status && before?.url === state.url && before?.error === state.error) return;
  for (const listener of listeners) {
    try { listener(modelId, state); } catch (error) { console.error(error); }
  }
}

function groupKeys(modelIds: string[]): Array<[boolean, string[]]> {
  const cloud: string[] = [], builtin: string[] = [];
  for (const id of new Set(modelIds)) {
    const key = catalogKeyOf(id);
    if (!key) continue;
    (key.builtin ? builtin : cloud).push(key.key);
  }
  const out: Array<[boolean, string[]]> = [];
  if (cloud.length) out.push([false, cloud]);
  if (builtin.length) out.push([true, builtin]);
  return out;
}

async function askStatus(builtin: boolean, keys: string[]): Promise<Record<string, WebState>> {
  const out: Record<string, WebState> = {};
  for (const part of chunks(keys, builtin ? MAX_BUILTIN_IDS : MAX_IDS)) {
    const ids = part.map((k) => encodeURIComponent(k)).join(',');
    const answer = await getJson<Record<string, WebState>>(`/models/web/status?ids=${ids}${builtin ? '&builtin=1' : ''}${scopeQuery()}`);
    for (const [key, state] of Object.entries(answer)) out[modelIdOfKey(key, builtin)] = state;
  }
  return out;
}

async function askRequest(builtin: boolean, keys: string[]): Promise<Record<string, WebState>> {
  const out: Record<string, WebState> = {};
  for (const part of chunks(keys, MAX_IDS)) {
    const answer = await postJson<Record<string, WebState>>('/models/web/request', { ids: part, builtin, ...scopeBody() });
    for (const [key, state] of Object.entries(answer)) out[modelIdOfKey(key, builtin)] = state;
  }
  return out;
}

/**
 * What is worth asking the queue for: never asked, failed in a way a new try may pass, or converted
 * in an older layout. A model that cannot convert until it changes (`permanentFailure`) is not asked
 * again: the catalog would answer the same. An old bundle is asked for again once a session.
 */
function wantsRequest(state: WebState | undefined, id: string): boolean {
  if (!state || state.status === 'none' || state.status === 'unavailable') return true;
  if (state.status === 'error') return !permanentFailure(state.code);
  return outdated(state) && !upgradeAsked.has(id);
}

/** A bundle of an older layout, unless its re-conversion already failed for good. */
function outdated(state: WebState): boolean {
  return state.status === 'done' && (state.format ?? 1) < WEB_FORMAT && !(state.lastError && permanentFailure(state.lastCode));
}

/** Old-layout bundles already asked for again this session: the answer still names the old one. */
const upgradeAsked = new Set<string>();

/**
 * The queue's answer for these models, asking it to convert the ones that need it. Models
 * already known here are not asked about again unless `refresh` is set. The poller then
 * keeps the pending ones current; `subscribeWebStates` hears when each one settles.
 */
export async function ensureWebBundles(modelIds: string[], { request = true, refresh = false } = {}): Promise<Record<string, WebState>> {
  const out: Record<string, WebState> = {};
  const ask: string[] = [];
  for (const id of new Set(modelIds)) {
    if (!catalogKeyOf(id)) continue;
    const known = states.get(id);
    if (known && !refresh && !(request && wantsRequest(known, id))) out[id] = known; else ask.push(id);
  }
  for (const [builtin, keys] of groupKeys(ask)) {
    let answer: Record<string, WebState>;
    try {
      answer = await askStatus(builtin, keys);
      if (request) {
        const wantedIds = Object.entries(answer).filter(([id, state]) => wantsRequest(state, id)).map(([id]) => id);
        for (const id of wantedIds) if (outdated(answer[id])) upgradeAsked.add(id);
        const wanted = wantedIds.map((id) => catalogKeyOf(id)!.key);
        if (wanted.length) Object.assign(answer, await askRequest(builtin, wanted));
      }
    } catch (error) {
      // The catalog could not be asked: say so per model rather than pretend they are missing.
      const reason = error instanceof Error ? error.message : String(error);
      answer = Object.fromEntries(keys.map((key) => [modelIdOfKey(key, builtin), { status: 'unavailable', error: reason } as WebState]));
    }
    for (const key of keys) {
      const id = modelIdOfKey(key, builtin);
      const state = answer[id] ?? { status: 'missing' } as WebState;
      publish(id, state);
      out[id] = state;
    }
  }
  schedulePoll();
  return out;
}

/** Asks the queue again for one model after a failure. */
export async function retryWebBundle(modelId: string): Promise<WebState | undefined> {
  const key = catalogKeyOf(modelId);
  if (!key) return undefined;
  try {
    const answer = await askRequest(key.builtin, [key.key]);
    const state = answer[modelId] ?? { status: 'missing' } as WebState;
    publish(modelId, state);
    schedulePoll();
    return state;
  } catch (error) {
    const state: WebState = { status: 'unavailable', error: error instanceof Error ? error.message : String(error) };
    publish(modelId, state);
    return state;
  }
}

function schedulePoll(): void {
  if (pollTimer !== undefined || !pending.size) return;
  if (!pollStarted) pollStarted = Date.now();
  const delay = POLL_DELAYS_MS[Math.min(pollDelay, POLL_DELAYS_MS.length - 1)];
  pollTimer = setTimeout(() => { pollTimer = undefined; void poll(); }, delay);
}

async function poll(): Promise<void> {
  if (!pending.size) { pollDelay = 0; pollStarted = 0; return; }
  const ids = [...pending];
  let settled = false;
  for (const [builtin, keys] of groupKeys(ids)) {
    try {
      const answer = await askStatus(builtin, keys);
      for (const key of keys) {
        const id = modelIdOfKey(key, builtin);
        const state = answer[id] ?? { status: 'missing' } as WebState;
        if (state.status !== 'queued' && state.status !== 'running') settled = true;
        publish(id, state);
      }
    } catch {
      // A failed poll is not an answer; the next one may succeed.
    }
  }
  if (pending.size && Date.now() - pollStarted > POLL_GIVE_UP_MS) {
    for (const id of [...pending]) publish(id, { status: 'unavailable', error: t('#CatalogApiConversionTimeout') });
  }
  pollDelay = settled ? 0 : pollDelay + 1;
  schedulePoll();
}

/** Stops waiting and forgets everything — for tests and for leaving the page. */
export function resetWebStates(): void {
  if (pollTimer !== undefined) clearTimeout(pollTimer);
  pollTimer = undefined; pollDelay = 0; pollStarted = 0;
  pending.clear(); states.clear(); upgradeAsked.clear(); catalogProject = null; catalogSnapshot = null;
}
